"use client"

import React, { useState, useEffect } from 'react'
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import {
  User,
  Building2,
  Mail,
  Phone,
  Cake,
  Home,
  Users,
  School,
  Loader2,
  Save,
  ShieldCheck,
  GraduationCap,
  Globe,
  BookOpen,
  X,
} from "lucide-react"
import { useUserProfile, useUserActions } from "@/hooks/stores/userStore"
import { AuthStore } from "@/hooks/stores/AuthStore"
import { StorageService } from "@/hooks/services/storageService"
import { COUNTRIES, EXAM_BODIES, CLASS_LEVELS, getEligibleClasses } from "@/data/educationSystems"
import { cn } from "@/lib/utils"

interface EditPersonalInfoModalProps {
  isOpen: boolean
  onClose: () => void
}

interface PersonalInfoFormData {
  name: string
  email: string
  phoneNumber: string
  dateOfBirth: string
  address: string
  schoolName: string
  parentName: string
  parentEmail: string
  parentPhone: string
  // Academic
  country: string
  examBody: string
  classLevel: string
  additionalClasses: string[]
}

export function EditPersonalInfoModal({ isOpen, onClose }: EditPersonalInfoModalProps) {
  const userProfile = useUserProfile()
  const { updateProfile } = useUserActions()

  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [formData, setFormData] = useState<PersonalInfoFormData>({
    name: '',
    email: '',
    phoneNumber: '',
    dateOfBirth: '',
    address: '',
    schoolName: '',
    parentName: '',
    parentEmail: '',
    parentPhone: '',
    country: 'NG',
    examBody: 'WAEC',
    classLevel: 'SS2',
    additionalClasses: [],
  })

  // Load from both UserStore and StorageService on open
  useEffect(() => {
    if (!isOpen) return
    const currentUser = AuthStore.getState().currentUser
    const students: any[] = StorageService.read('students') ?? []
    const student = students.find(
      (s) => s.userId === currentUser?._id || s.email === currentUser?.email
    )
    const prefs = student?.preferences ?? {}

    let formattedDate = ''
    if (userProfile?.dateOfBirth) {
      try {
        const d = new Date(userProfile.dateOfBirth as any)
        if (!isNaN(d.getTime())) formattedDate = d.toISOString().split('T')[0]
      } catch { /* ignore */ }
    }

    setFormData({
      name: userProfile?.name || `${student?.firstName ?? ''} ${student?.lastName ?? ''}`.trim() || '',
      email: userProfile?.email || student?.email || '',
      phoneNumber: (userProfile as any)?.phoneNumber || student?.phone || '',
      dateOfBirth: formattedDate,
      address: (userProfile as any)?.address || student?.address?.street || '',
      schoolName: (userProfile as any)?.schoolName || '',
      parentName: (userProfile as any)?.parentName || '',
      parentEmail: (userProfile as any)?.parentEmail || '',
      parentPhone: (userProfile as any)?.parentPhone || '',
      country: prefs.country || 'NG',
      examBody: prefs.examBody || 'WAEC',
      classLevel: prefs.classLevel || student?.classLevel || 'SS2',
      additionalClasses: (prefs.classes ?? []).filter((c: string) => c !== (prefs.classLevel || student?.classLevel || 'SS2')),
    })
    setError(null)
  }, [isOpen, userProfile])

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target
    setFormData((prev) => {
      const next = { ...prev, [name]: value }
      // Reset dependent fields when country or examBody changes
      if (name === 'country') {
        const bodies = EXAM_BODIES[value] ?? []
        next.examBody = bodies[0]?.code ?? ''
        next.classLevel = CLASS_LEVELS[next.examBody]?.[0] ?? ''
        next.additionalClasses = []
      }
      if (name === 'examBody') {
        next.classLevel = CLASS_LEVELS[value]?.[0] ?? ''
        next.additionalClasses = []
      }
      if (name === 'classLevel') {
        next.additionalClasses = []
      }
      return next
    })
    if (error) setError(null)
  }

  const toggleAdditionalClass = (cls: string) => {
    setFormData((prev) => {
      const exists = prev.additionalClasses.includes(cls)
      const next = exists
        ? prev.additionalClasses.filter((c) => c !== cls)
        : [...prev.additionalClasses, cls]
      return { ...prev, additionalClasses: next }
    })
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setIsLoading(true)
    setError(null)

    try {
      if (!formData.name.trim()) throw new Error('Full name is required')
      if (!formData.email.trim()) throw new Error('Email is required')

      // 1. Update UserStore (display name, email, etc.)
      updateProfile({
        name: formData.name.trim(),
        email: formData.email.trim(),
        ...(formData.dateOfBirth ? { dateOfBirth: new Date(formData.dateOfBirth) as any } : {}),
      } as any)

      // 2. Persist academic details + personal info to StorageService students
      const currentUser = AuthStore.getState().currentUser
      const students: any[] = StorageService.read('students') ?? []
      const idx = students.findIndex(
        (s) => s.userId === currentUser?._id || s.email === currentUser?.email
      )
      if (idx !== -1) {
        const classes = [formData.classLevel, ...formData.additionalClasses].filter(Boolean)
        students[idx] = {
          ...students[idx],
          phone: formData.phoneNumber,
          preferences: {
            ...(students[idx].preferences ?? {}),
            country: formData.country,
            examBody: formData.examBody,
            classLevel: formData.classLevel,
            classes,
          },
          updatedAt: new Date().toISOString(),
        }
        StorageService.write('students', students)
      }

      onClose()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update profile')
    } finally {
      setIsLoading(false)
    }
  }

  // Eligible additional classes (all eligible minus the current class itself)
  const eligibleExtras = getEligibleClasses(formData.examBody, formData.classLevel)
    .filter((c) => c !== formData.classLevel)

  const examBodiesForCountry = EXAM_BODIES[formData.country] ?? []
  const classLevelsForExam = CLASS_LEVELS[formData.examBody] ?? []

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !isLoading && !open && onClose()}>
      <DialogContent className="sm:max-w-[760px] p-0 gap-0 overflow-hidden bg-slate-50 border-none shadow-2xl">
        <DialogHeader className="p-6 bg-white border-b border-slate-100">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-indigo-50 rounded-lg">
              <User className="w-6 h-6 text-indigo-600" />
            </div>
            <div>
              <DialogTitle className="text-xl font-bold text-slate-900">Edit Profile</DialogTitle>
              <DialogDescription className="text-slate-500">
                Update your personal details, academic settings, and guardian contacts.
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="flex flex-col max-h-[72vh]">
          <div className="flex-1 overflow-y-auto p-6 space-y-8">
            {error && (
              <div className="flex items-center gap-2 bg-red-50 border border-red-100 text-red-600 px-4 py-3 rounded-xl text-sm">
                <ShieldCheck className="w-4 h-4 shrink-0" />
                <p><strong>Error:</strong> {error}</p>
              </div>
            )}

            {/* ── Student Information ───────────────────────────────────── */}
            <section className="space-y-4">
              <SectionHeader icon={<GraduationCap className="w-5 h-5 text-indigo-500" />} title="Student Information" />

              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <FormInput
                  label="Full Name"
                  id="name" name="name"
                  value={formData.name} onChange={handleChange}
                  icon={<User className="w-4 h-4" />}
                  placeholder="e.g. Alex Johnson"
                  required disabled={isLoading}
                />
                <FormInput
                  label="Email Address"
                  id="email" name="email" type="email"
                  value={formData.email} onChange={handleChange}
                  icon={<Mail className="w-4 h-4" />}
                  placeholder="student@school.com"
                  required disabled={isLoading}
                />
                <FormInput
                  label="Phone Number"
                  id="phoneNumber" name="phoneNumber" type="tel"
                  value={formData.phoneNumber} onChange={handleChange}
                  icon={<Phone className="w-4 h-4" />}
                  placeholder="+234..."
                  disabled={isLoading}
                />
                <FormInput
                  label="Date of Birth"
                  id="dateOfBirth" name="dateOfBirth" type="date"
                  value={formData.dateOfBirth} onChange={handleChange}
                  icon={<Cake className="w-4 h-4" />}
                  disabled={isLoading}
                />
                <FormInput
                  label="School Name"
                  id="schoolName" name="schoolName"
                  value={formData.schoolName} onChange={handleChange}
                  icon={<School className="w-4 h-4" />}
                  placeholder="Highlands Academy"
                  disabled={isLoading}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="address" className="text-sm font-medium text-slate-700 ml-1">Residential Address</Label>
                <div className="relative group">
                  <Home className="absolute left-3 top-3 w-4 h-4 text-slate-400 group-focus-within:text-indigo-500 transition-colors" />
                  <textarea
                    id="address" name="address"
                    value={formData.address} onChange={handleChange}
                    className="w-full pl-10 pr-4 py-2 bg-white border border-slate-200 rounded-xl text-sm focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 outline-none transition-all min-h-[80px] resize-none disabled:opacity-50"
                    placeholder="Enter your full street address..."
                    disabled={isLoading}
                  />
                </div>
              </div>
            </section>

            {/* ── Academic Details ──────────────────────────────────────── */}
            <section className="space-y-4 pt-4 border-t border-slate-200/60">
              <SectionHeader icon={<BookOpen className="w-5 h-5 text-violet-500" />} title="Academic Details" />

              <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                {/* Country */}
                <div className="space-y-2">
                  <Label htmlFor="country" className="text-sm font-medium text-slate-700 ml-1">Country</Label>
                  <div className="relative group">
                    <Globe className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 group-focus-within:text-indigo-500 transition-colors" />
                    <select
                      id="country" name="country"
                      value={formData.country} onChange={handleChange}
                      className="w-full pl-10 pr-4 py-2 bg-white border border-slate-200 rounded-xl text-sm focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 outline-none transition-all appearance-none cursor-pointer disabled:opacity-50"
                      disabled={isLoading}
                    >
                      {COUNTRIES.map((c) => (
                        <option key={c.code} value={c.code}>{c.name}</option>
                      ))}
                    </select>
                  </div>
                </div>

                {/* Exam Body */}
                <div className="space-y-2">
                  <Label htmlFor="examBody" className="text-sm font-medium text-slate-700 ml-1">Exam Body</Label>
                  <div className="relative group">
                    <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 group-focus-within:text-indigo-500 transition-colors" />
                    <select
                      id="examBody" name="examBody"
                      value={formData.examBody} onChange={handleChange}
                      className="w-full pl-10 pr-4 py-2 bg-white border border-slate-200 rounded-xl text-sm focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 outline-none transition-all appearance-none cursor-pointer disabled:opacity-50"
                      disabled={isLoading}
                    >
                      {examBodiesForCountry.map((b) => (
                        <option key={b.code} value={b.code}>{b.code}</option>
                      ))}
                    </select>
                  </div>
                </div>

                {/* Current Class */}
                <div className="space-y-2">
                  <Label htmlFor="classLevel" className="text-sm font-medium text-slate-700 ml-1">Current Class</Label>
                  <div className="relative group">
                    <GraduationCap className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 group-focus-within:text-indigo-500 transition-colors" />
                    <select
                      id="classLevel" name="classLevel"
                      value={formData.classLevel} onChange={handleChange}
                      className="w-full pl-10 pr-4 py-2 bg-white border border-slate-200 rounded-xl text-sm focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 outline-none transition-all appearance-none cursor-pointer disabled:opacity-50"
                      disabled={isLoading}
                    >
                      {classLevelsForExam.map((cl) => (
                        <option key={cl} value={cl}>{cl}</option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>

              {/* Additional classes (lower classes student also studies) */}
              {eligibleExtras.length > 0 && (
                <div className="space-y-2">
                  <Label className="text-sm font-medium text-slate-700 ml-1">
                    Also enrolled in
                    <span className="text-slate-400 font-normal ml-1">(optional — add lower classes)</span>
                  </Label>
                  <div className="flex flex-wrap gap-2">
                    {eligibleExtras.map((cls) => {
                      const active = formData.additionalClasses.includes(cls)
                      return (
                        <button
                          key={cls}
                          type="button"
                          onClick={() => toggleAdditionalClass(cls)}
                          disabled={isLoading}
                          className={cn(
                            "px-3 py-1.5 rounded-lg text-sm font-medium border transition-all",
                            active
                              ? "bg-violet-600 text-white border-violet-600"
                              : "bg-white text-slate-600 border-slate-200 hover:border-violet-300"
                          )}
                        >
                          {active && <X className="w-3 h-3 inline mr-1" />}
                          {cls}
                        </button>
                      )
                    })}
                  </div>
                  <p className="text-xs text-slate-400 ml-1">
                    Selected: {[formData.classLevel, ...formData.additionalClasses].join(', ')}
                  </p>
                </div>
              )}
            </section>

            {/* ── Guardian Information ──────────────────────────────────── */}
            <section className="space-y-4 pt-4 border-t border-slate-200/60">
              <SectionHeader icon={<Users className="w-5 h-5 text-teal-500" />} title="Guardian Information" />

              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <div className="md:col-span-2">
                  <FormInput
                    label="Parent/Guardian Name"
                    id="parentName" name="parentName"
                    value={formData.parentName} onChange={handleChange}
                    icon={<User className="w-4 h-4" />}
                    placeholder="Jane Johnson"
                    disabled={isLoading}
                  />
                </div>
                <FormInput
                  label="Guardian Email"
                  id="parentEmail" name="parentEmail" type="email"
                  value={formData.parentEmail} onChange={handleChange}
                  icon={<Mail className="w-4 h-4" />}
                  placeholder="parent@example.com"
                  disabled={isLoading}
                />
                <FormInput
                  label="Guardian Phone"
                  id="parentPhone" name="parentPhone" type="tel"
                  value={formData.parentPhone} onChange={handleChange}
                  icon={<Phone className="w-4 h-4" />}
                  placeholder="+234..."
                  disabled={isLoading}
                />
              </div>
            </section>
          </div>

          <DialogFooter className="p-6 bg-white border-t border-slate-100 flex items-center gap-3">
            <Button
              type="button"
              variant="ghost"
              onClick={onClose}
              disabled={isLoading}
              className="rounded-xl text-slate-500 hover:bg-slate-50"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={isLoading}
              className="bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl px-8 shadow-lg shadow-indigo-200 transition-all active:scale-[0.98]"
            >
              {isLoading ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Saving...</>
              ) : (
                <><Save className="w-4 h-4 mr-2" /> Save Changes</>
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}

// ─── Helper components ────────────────────────────────────────────────────────

function SectionHeader({ icon, title }: { icon: React.ReactNode; title: string }) {
  return (
    <div className="flex items-center gap-2 mb-2">
      {icon}
      <h3 className="text-sm font-bold uppercase tracking-wider text-slate-500">{title}</h3>
    </div>
  )
}

function FormInput({ label, icon, ...props }: any) {
  return (
    <div className="space-y-2">
      <Label htmlFor={props.id} className="text-sm font-medium text-slate-700 ml-1">
        {label} {props.required && '*'}
      </Label>
      <div className="relative group">
        <div className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 group-focus-within:text-indigo-500 transition-colors">
          {icon}
        </div>
        <Input
          {...props}
          className={cn(
            "pl-10 bg-white border-slate-200 rounded-xl text-sm focus-visible:ring-2 focus-visible:ring-indigo-500/20 focus-visible:border-indigo-500 transition-all",
            props.disabled && "opacity-50"
          )}
        />
      </div>
    </div>
  )
}
